import {
  FunctionComponent,
  useRef,
  useCallback,
  useState,
  ChangeEvent,
} from 'react'
import { MdCheck } from 'react-icons/md'
import { IoMdEyeOff, IoMdEye } from 'react-icons/io'
import { useHistory, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import { ISignIn } from 'modules/auth/domain/usecases/SignInImpl'
import LoadingBackdrop from 'shared/components/LoadingBackdrop'
import SnackBar from 'shared/components/SnackBar'
import iconKey from 'shared/assets/icons/icon-key.svg'
import Form, { IProps as FormProps } from 'shared/components/Form'
import Input from 'shared/components/Input'
import ValidatePassword from 'shared/components/ValidatePassword'
import { colors } from 'shared/styles/colors'
import { decryptOnlyWithKey } from 'shared/utils/crypt'

import {
  ContainerForm,
  StyledButton,
  StyledFeedback,
  StyledPage,
  StyledCenter,
  Icon,
  ContainerInput,
  ButtonShowPassword,
  HelperMessage,
  ContainerButton,
} from './styles'
import { useNewPassword } from 'modules/user/hooks/newPassword'
import { ICreatePassword } from 'modules/user/domain/usecases/CreatePasswordImpl'
import { IAuthContextState } from 'modules/auth/hooks/auth'

type TProp = {
  createPassword: ICreatePassword
  signIn: ISignIn
  signInStore: IAuthContextState['signIn']
}

const NewPasswordScreen: FunctionComponent<TProp> = ({
  createPassword,
  signIn,
  signInStore,
}) => {
  const history = useHistory()
  const {
    newUserPassword,
    setNewPassword,
    isPasswordValid,
    confirmPassword,
    setConfirmPassword,
  } = useNewPassword()
  const inputNewPasswordRef = useRef(null)
  const inputConfirmPasswordRef = useRef(null)
  const [loading, setLoading] = useState(false)
  const [openErrorMessage, setOpenErrorMessage] = useState(false)
  const [snackMessage, setSnackMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [newPasswordOk, setNewPasswordOk] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)

  const { hash_autenticator } =
    useParams<{ hash_autenticator: string | undefined }>()

  const handleSubmit = useCallback(
    async (values: FormProps['values']) => {
      setErrorMessage('')
      setLoading(true)
      try {
        const schema = Yup.object().shape({
          newPassword: Yup.string().nullable(),
          confirmPassword: Yup.string().oneOf(
            [Yup.ref('newPassword'), null],
            'As senhas não são iguais. Tente novamente.',
          ),
        })

        await schema.validate(values, {
          abortEarly: false,
        })

        const response = await createPassword.execute({
          hashAutenticator: hash_autenticator,
          password: values.newPassword,
        })

        if (response.error) {
          setErrorMessage(response.error)

          return
        }

        setNewPasswordOk(true)
      } catch (err: unknown) {
        setLoading(false)
        if (err instanceof Yup.ValidationError) {
          setErrorMessage(err.message)
        } else if (err instanceof Error) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('Erro tentar setar a nova senha')
        }
      }
    },
    [createPassword, hash_autenticator],
  )

  const handleClickFeedBackButton = useCallback(async () => {
    try {
      const { email } = decryptOnlyWithKey(
        decodeURIComponent(hash_autenticator),
      )

      const response = await signIn.execute({
        password: newUserPassword,
        username: email,
      })

      signInStore({ ...response, user: email })

      history.push('/lotes')
    } catch (e: unknown) {
      if (e instanceof Error) {
        setSnackMessage(e.message)
      } else {
        setSnackMessage('Erro ao tentar fazer o login')
      }
      setOpenErrorMessage(true)
    }
  }, [hash_autenticator, history, newUserPassword, signIn, signInStore])

  if (newPasswordOk)
    return (
      <StyledCenter>
        <StyledFeedback
          colorIcon={colors.white}
          sizeIcon={70}
          title="Senha alterada com sucesso!"
          onClickButton={handleClickFeedBackButton}
        />
      </StyledCenter>
    )

  return (
    <>
      <SnackBar
        open={openErrorMessage}
        setOpen={setOpenErrorMessage}
        severity="error"
        message={snackMessage}
        horizontal="right"
      />
      {loading && <LoadingBackdrop open={loading} color={colors.green} />}
      <StyledPage icon={iconKey} title="Nova senha">
        <div style={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
          <Form
            onSubmit={handleSubmit}
            initialValues={{
              newPassword: '',
              confirmPassword: '',
            }}
            validationSchema={() => ({
              newPassword: Yup.string().nullable(),
              confirmPassword: Yup.string().nullable(),
            })}
          >
            {formControl => (
              <ContainerForm>
                <ContainerInput>
                  <Input
                    ref={inputNewPasswordRef}
                    placeholder="Digite sua nova senha"
                    name="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    formControl={formControl}
                    errorMessage={''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setNewPassword(e.target.value)
                    }}
                  >
                    <ButtonShowPassword
                      onClick={() => {
                        setShowNewPassword(!showNewPassword)
                      }}
                    >
                      <Icon>
                        {!showNewPassword ? <IoMdEye /> : <IoMdEyeOff />}
                      </Icon>
                    </ButtonShowPassword>
                  </Input>
                </ContainerInput>
                <ContainerInput>
                  <Input
                    ref={inputConfirmPasswordRef}
                    placeholder="Confirme sua nova senha"
                    name="confirmPassword"
                    type={showConfirmNewPassword ? 'text' : 'password'}
                    formControl={formControl}
                    errorMessage={''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setConfirmPassword(e.target.value)
                    }}
                  >
                    <ButtonShowPassword
                      onClick={() => {
                        setShowConfirmNewPassword(!showConfirmNewPassword)
                      }}
                    >
                      <Icon>
                        {!showConfirmNewPassword ? <IoMdEye /> : <IoMdEyeOff />}
                      </Icon>
                    </ButtonShowPassword>
                  </Input>
                </ContainerInput>

                {errorMessage && <HelperMessage>{errorMessage}</HelperMessage>}

                <ValidatePassword />

                <ContainerButton>
                  <StyledButton
                    color={
                      newUserPassword && confirmPassword && isPasswordValid
                        ? colors.green
                        : colors.grayLight
                    }
                    type="submit"
                    onClick={formControl.handleSubmit}
                    disabled={
                      !(
                        newUserPassword &&
                        confirmPassword &&
                        isPasswordValid
                      ) || loading
                    }
                  >
                    <MdCheck /> Salvar
                  </StyledButton>
                </ContainerButton>
              </ContainerForm>
            )}
          </Form>
        </div>
      </StyledPage>
    </>
  )
}

export default NewPasswordScreen
